import {Dialog, DialogBackdrop, DialogPanel} from '@headlessui/react';
import classNames from 'classnames';
import React from 'react';

enum RefreshOpenState {
  CLOSING,
  CLOSED,
  OPEN,
}

// Returns 2x props. 1 for whether the modal is open, and another for whether it is visible which can be used to stop rendering the modal
// Enables the modal to animate out before it is removed from the DOM
export const useModalOpenManagement = (isOpen: Boolean) => {
  const [refreshOpenState, setRefreshOpenState] =
    React.useState<RefreshOpenState>(
      isOpen ? RefreshOpenState.OPEN : RefreshOpenState.CLOSED,
    );

  const setOpen = (open: boolean) => {
    if (open) {
      setRefreshOpenState(RefreshOpenState.OPEN);
    } else {
      setRefreshOpenState(RefreshOpenState.CLOSING);
    }
  };

  React.useEffect(() => {
    if (refreshOpenState === RefreshOpenState.CLOSING) {
      setTimeout(() => {
        setRefreshOpenState(RefreshOpenState.CLOSED);
      }, 200);
    }
  }, [refreshOpenState]);

  return {
    isOpen: refreshOpenState === RefreshOpenState.OPEN,
    isRendered: refreshOpenState !== RefreshOpenState.CLOSED,
    setOpen,
  };
};

interface IProps {
  children: React.ReactNode;
  maxWidth?:
    | 'sm'
    | 'md'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl'
    | '7xl';
  open: boolean;
  onClose: () => void;
}

export const ModalDialog: React.FC<IProps> = (props) => {
  const maxWidth = getMaxWidth(props.maxWidth);
  return (
    <Dialog open={props.open} onClose={props.onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={classNames(
              'relative transform mx-4 overflow-hidden rounded-lg w-full bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95',
              maxWidth,
            )}>
            {props.children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const getMaxWidth = (maxWidth: string | undefined) => {
  if (maxWidth === 'sm') {
    return 'sm:max-w-sm';
  } else if (maxWidth === 'md') {
    return 'sm:max-w-md';
  } else if (maxWidth === 'xl') {
    return 'sm:max-w-xl';
  } else if (maxWidth === '2xl') {
    return 'sm:max-w-2xl';
  } else if (maxWidth === '3xl') {
    return 'sm:max-w-3xl';
  } else if (maxWidth === '4xl') {
    return 'sm:max-w-4xl';
  } else if (maxWidth === '5xl') {
    return 'sm:max-w-5xl';
  } else if (maxWidth === '6xl') {
    return 'sm:max-w-6xl';
  } else if (maxWidth === '7xl') {
    return 'sm:max-w-7xl';
  } else {
    return 'sm:max-w-lg';
  }
};
